import { useState } from "react";
import { useCustomMutation, type useTranslate } from "@refinedev/core";

import { CeIcon } from "./CeIcon";

// De rij snelle AI-acties boven het document (ce-ai-strip in de bron): de vijf dingen die je het
// vaakst met een brief wilt doen, één klik ver. Ze doen hetzelfde als de gelijknamige keuzes in de
// AI-kolom; die staat er voor het fijnere werk (eigen instructie, voorstel vergelijken, veldcheck).
//
// Dezelfde grens als overal in dit scherm: er wordt niets automatisch toegepast. Het voorstel komt
// terug met Toepassen/Annuleren erbij, en een voorstel dat de server niet placeholder-veilig noemt
// kan niet worden toegepast — anders raakt een brief zijn velden kwijt en valt dat pas op als hij de
// deur uit is.

type Translate = ReturnType<typeof useTranslate>;

interface Voorstel {
  proposalHtml: string;
  summary: string;
  warnings: string[];
  placeholdersPreserved: boolean;
}

/** De vijf snelle acties uit de bron, in dezelfde volgorde. */
const ACTIES: { actie: string; icoon: string; sleutel: string }[] = [
  { actie: "friendlier", icoon: "ai", sleutel: "communicatieAi.strip.friendlier" },
  { actie: "shorter", icoon: "scissors", sleutel: "communicatieAi.strip.shorter" },
  { actie: "check", icoon: "check", sleutel: "communicatieAi.strip.check" },
  { actie: "privacy", icoon: "shield", sleutel: "communicatieAi.strip.privacy" },
  { actie: "email", icoon: "mail", sleutel: "communicatieAi.strip.email" },
];

export function CommAiStrip({
  templateId,
  getBodyHtml,
  onApplyBody,
  t,
  disabled,
}: {
  templateId: string;
  getBodyHtml: () => string;
  onApplyBody: (html: string) => void;
  t: Translate;
  disabled?: boolean;
}) {
  const { mutate, isLoading } = useCustomMutation();
  const [voorstel, setVoorstel] = useState<Voorstel | null>(null);
  const [fout, setFout] = useState<string | null>(null);

  function vraag(actie: string) {
    setVoorstel(null);
    setFout(null);
    mutate(
      {
        url: "communicatie/templates/ai/rewrite",
        method: "post",
        values: { templateId, action: actie, fullTemplateHtml: getBodyHtml() },
        successNotification: false,
        errorNotification: false,
      },
      {
        onSuccess: (res) => setVoorstel(res.data as unknown as Voorstel),
        onError: (e) => setFout(e?.message ?? t("communicatieAi.error.generic")),
      },
    );
  }

  return (
    <>
      <div className="ce-ai-strip">
        {ACTIES.map((a) => (
          <button key={a.actie} type="button" disabled={disabled || isLoading} onClick={() => vraag(a.actie)}>
            <CeIcon naam={a.icoon} />
            {t(a.sleutel)}
          </button>
        ))}
      </div>

      {isLoading && <p className="ce-ai-busy">{t("communicatieAi.busy")}</p>}

      {fout && (
        <div className="comm-editor__alert comm-editor__alert--error" role="alert">
          {fout}
        </div>
      )}

      {voorstel && (
        <div className="ce-ai-proposal" role="status">
          <div className="ce-ai-sub">{voorstel.summary}</div>
          {voorstel.warnings.map((w) => (
            <div key={w} className="ce-ai-notice">
              {w}
            </div>
          ))}
          {!voorstel.placeholdersPreserved && (
            <div className="ce-ai-notice">{t("communicatieAi.notPlaceholderSafe")}</div>
          )}
          <div className="ce-ai-acts">
            <button
              type="button"
              className="ce-btn ce-primary"
              disabled={!voorstel.placeholdersPreserved}
              title={voorstel.placeholdersPreserved ? undefined : t("communicatieAi.notPlaceholderSafe")}
              onClick={() => {
                onApplyBody(voorstel.proposalHtml);
                setVoorstel(null);
              }}
            >
              {t("communicatieAi.apply")}
            </button>
            <button type="button" className="ce-btn" onClick={() => setVoorstel(null)}>
              {t("common.cancel")}
            </button>
          </div>
        </div>
      )}
    </>
  );
}
